import {ChangeDetectionStrategy, Component, inject, signal} from '@angular/core';
import {RouterLink} from '@angular/router';
import {TranslationService, ApiService, DataService} from '../core/services';
import {NgOptimizedImage} from '@angular/common';

@Component({
  selector: 'app-footer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, NgOptimizedImage],
  template: `
    <footer class="bg-neutral-900 border-t border-neutral-800" role="contentinfo">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <!-- Brand -->
          <div class="md:col-span-1">
            <a
              routerLink="/"
              class="flex items-center gap-2 text-white font-bold text-xl"
              aria-label="JKU Racing homepage"
            >
              <!-- Optimized SVG logo from /logos/rainbow -->
              <img
                ngSrc="/logos/logo_on-dark-bg_borderless.svg"
                width="1500"
                height="701"
                alt="JKU Racing logo"
                class="h-8 md:h-10 w-auto"
                decoding="async"
              />
            </a>
            <p class="mt-4 text-neutral-400 text-sm">
              Formula Student Team at <a href="https://www.jku.at/" target="_blank"
                                         class="hover:text-white transition-colors">Johannes Kepler University Linz</a>
            </p>
          </div>

          <!-- Navigation -->
          <div>
            <h3 class="text-white font-semibold mb-4">Navigation</h3>
            <ul class="space-y-2">
              <li>
                <a routerLink="/" class="text-neutral-400 hover:text-white transition-colors">
                  {{ t()('nav.home') }}
                </a>
              </li>
              <li>
                <a [routerLink]="['/season', this.data.currentSeason()]"
                   class="text-neutral-400 hover:text-white transition-colors">
                  {{ t()('nav.team') }}
                </a>
              </li>
              <li>
                <a routerLink="/sponsoring" class="text-neutral-400 hover:text-white transition-colors">
                  {{ t()('nav.sponsoring') }}
                </a>
              </li>
              <li>
                <a routerLink="/team/join" class="text-neutral-400 hover:text-white transition-colors">
                  {{ t()('nav.joinUs') }}
                </a>
              </li>
              <li>
                <a routerLink="/imprint" class="text-neutral-400 hover:text-white transition-colors">
                  {{ t()('nav.imprint') }}
                </a>
              </li>
              <li>
                <a routerLink="/privacy" class="text-neutral-400 hover:text-white transition-colors">
                  {{ t()('nav.privacy') }}
                </a>
              </li>
            </ul>
          </div>

          <!-- Contact -->
          <div>
            <h3 class="text-white font-semibold mb-4">{{ t()('footer.contact') }}</h3>
            <address class="not-italic text-neutral-400 text-sm space-y-2">
              <p>JKU Racing Team</p>
              <p>Johannes Kepler University</p>
              <p>Altenberger Straße 69</p>
              <p>4040 Linz, Austria</p>
              <p class="pt-2">
                <a href="mailto:office@jkuracing.at" class="text-accent-500 hover:text-accent-400 transition-colors">
                  office&#64;jkuracing.at
                </a>
              </p>
            </address>
          </div>

          <!-- Social -->
          <div>
            <h3 class="text-white font-semibold mb-4">{{ t()('footer.followUs') }}</h3>
            <div class="flex gap-4">
              <a
                href="https://www.instagram.com/jku_racing"
                target="_blank"
                rel="noopener noreferrer"
                class="text-neutral-400 hover:text-white transition-colors"
                aria-label="Instagram"
              >
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/company/jku-racing-team/posts/?feedView=all"
                target="_blank"
                rel="noopener noreferrer"
                class="text-neutral-400 hover:text-white transition-colors"
                aria-label="LinkedIn"
              >
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
              <a
                href="https://www.youtube.com/@jkuracing"
                target="_blank"
                rel="noopener noreferrer"
                class="text-neutral-400 hover:text-white transition-colors"
                aria-label="YouTube"
              >
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        <!-- Copyright -->
        <div class="mt-12 pt-8 border-t border-neutral-800 text-center text-neutral-500 text-sm">
          <p>&copy; {{ currentYear }} JKU Racing Team. {{ t()('footer.rights') }}</p>
        </div>
      </div>
    </footer>
  `,
})
export class FooterComponent {
  protected readonly data = inject(DataService);
  protected readonly translationService = inject(TranslationService);
  protected readonly t = this.translationService.t;
  protected readonly currentYear = new Date().getFullYear();
}
